import { useCallback, useEffect, useState } from 'react';
import { API_URL } from '../api/config';

export interface AccessRecord {
  id: string;
  usuarioNome: string;
  usuarioDocumento: string;
  usuarioEmail: string;
  loja: string;
  sala: string;
  /** Palestra em curso na sala quando a pessoa entrou. Vazia se nao havia sessao. */
  palestra?: string | null;
  palestraChave?: string | null;
  dataEntrada: string;
  horarioEntrada: string;
  dataSaida: string | null;
  horarioSaida: string | null;
  tempoPermancencia: string | null;
  operador: string;
  entrySearchMethod: string;
}

export interface AccessListResponse {
  data: AccessRecord[];
  total: number;
  page: number;
  totalPages: number;
  hasMore: boolean;
}

export interface AccessFilters {
  userName?: string;
  store?: string;
  startDate?: string;
  endDate?: string;
}

export const useAccessList = () => {
  const [acessos, setAcessos] = useState<AccessRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState<AccessFilters>({});

  const loadAcessos = useCallback(async (page = 1, reset = false, newFilters?: AccessFilters) => {
    if (page === 1 || reset) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    setError(null);

    try {
      const filtersToUse = newFilters !== undefined ? newFilters : filters;
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        ...(filtersToUse.store && { store: filtersToUse.store }),
        ...(filtersToUse.userName && { userName: filtersToUse.userName }),
        ...(filtersToUse.startDate && { startDate: filtersToUse.startDate }),
        ...(filtersToUse.endDate && { endDate: filtersToUse.endDate })
      });

      const response = await fetch(`${API_URL}access/list?${params}`);
      
      if (!response.ok) {
        throw new Error(`Erro ${response.status}: ${response.statusText}`);
      }

      const data: AccessListResponse = await response.json();

      if (reset || page === 1) {
        // Primeira carga ou filtro aplicado
        setAcessos(data.data || []);
      } else {
        // Carregar mais - concatena com dados existentes
        setAcessos(prev => [...prev, ...(data.data || [])]);
      }

      setTotal(data.total || 0);
      setCurrentPage(page);
      setHasMore(data.hasMore !== false && page < (data.totalPages || 1));

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar acessos';
      setError(errorMessage);
      console.error('Erro ao carregar acessos:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [filters]);

  const handleLoadMore = useCallback(() => {
    if (!loadingMore && hasMore) {
      loadAcessos(currentPage + 1, false);
    }
  }, [loadAcessos, currentPage, loadingMore, hasMore]);

  const handleFilter = useCallback((newFilters: AccessFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
    loadAcessos(1, true, newFilters);
  }, [loadAcessos]);

  const handleClearFilters = useCallback(() => {
    const emptyFilters = {};
    setFilters(emptyFilters);
    setCurrentPage(1);
    loadAcessos(1, true, emptyFilters);
  }, [loadAcessos]);

  const refresh = useCallback(() => {
    setCurrentPage(1);
    loadAcessos(1, true);
  }, [loadAcessos]);

  // Carregamento inicial
  useEffect(() => {
    loadAcessos(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    acessos,
    loading,
    loadingMore,
    error,
    currentPage,
    hasMore,
    total,
    filters,
    loadAcessos,
    handleLoadMore,
    handleFilter,
    handleClearFilters,
    refresh
  };
}; 